import icn_pie_chart from "assets/images/icons/icn_pie_chart.svg";
import { ReactComponent as AllowIcon } from "assets/images/icons/icn_table_cell_Allow.svg";
import { ReactComponent as AllowSolidIcon } from "assets/images/icons/icn_table_cell_Allow_solid.svg";
import { ReactComponent as BlockIcon } from "assets/images/icons/icn_table_cell_Block_Red.svg";
import { ReactComponent as BlockSolidIcon } from "assets/images/icons/icn_table_cell_Block_Red_solid.svg";

import React, { useMemo, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import FirmwareComplianceChart from "components/FirmwareComplianceChart";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineWarnRectangleButton } from "components/buttons/OutlineWarnRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

const BottomActionBar = ({
  parentId,
  show,
  onCheckFormValid,
  onCancel,
  onApply,
}) => {
  useEffect(() => {
    const parentElement = document.getElementById(parentId);
    const bottomActionBarElement = document.getElementById("bottomActionBar");

    if (!parentElement || !bottomActionBarElement) return;

    const updateWidth = () => {
      bottomActionBarElement.style.width = `${parentElement.clientWidth - 32}px`;
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(parentElement);
    updateWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, [parentId]);

  return (
    <Box
      id="bottomActionBar"
      sx={{
        position: "fixed",
        bottom: "20px",
        width: 0,
        display: show ? "flex" : "none",
        justifyContent: "center",
        gap: 2,
        margin: "16px 0",
        padding: "16px",
        backgroundColor: "primary.lightBlue",
        transform: show ? "translateY(0)" : "translateY(80px)",
        transition: "transform 0.3s ease-in-out",
        zIndex: 1300,
      }}
    >
      <OutlineButton
        onClick={onCancel}
        sx={{ marginRight: "20px" }}
        width="100px"
        height="32px"
      >
        Cancel
      </OutlineButton>

      <SolidButton
        onClick={onApply}
        width="100px"
        height="32px"
        disabled={!onCheckFormValid()}
      >
        Apply
      </SolidButton>
    </Box>
  );
};

const AbnormalLocationAndAssestDisplacementDrawer = ({
  chartData = [],
  abnormalClientData = [],
  auth,
  refreshAbnormalLocationData,
}) => {
  const theme = useTheme();
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const { sendRequest } = useDataSourceManager();

  const [selectedActionMap, setSelectedActionMap] = useState({});
  const [showBottomActionBar, setShowBottomActionBar] = useState(false);

  const containerId = "abnormalLocationAndAssestDisplacementContainer";

  const pieChartData = Array.isArray(chartData) ? chartData : [];

  const totalCount = useMemo(() => {
    return pieChartData.reduce((sum, item) => {
      return sum + Number(item?.value || 0);
    }, 0);
  }, [pieChartData]);

  const percentages = useMemo(() => {
    const getValue = (name) => {
      return Number(
        pieChartData.find((item) => item.name === name)?.value || 0,
      );
    };

    if (!totalCount) {
      return {
        secured: 0,
        learning: 0,
        displaced: 0,
      };
    }

    return {
      secured: Math.round((getValue("Secured") / totalCount) * 100),
      learning: Math.round((getValue("Learning") / totalCount) * 100),
      displaced: Math.round((getValue("Displaced") / totalCount) * 100),
    };
  }, [pieChartData, totalCount]);

  const totalValue = String(totalCount);

  const getPercent = (name) => {
    if (name === "Secured") return `${percentages.secured}%`;
    if (name === "Learning") return `${percentages.learning}%`;
    if (name === "Displaced") return `${percentages.displaced}%`;
    return "0%";
  };

  const legendFormatter = (name) => {
    return `{name|${name}} {percent|${getPercent(name)}}`;
  };

  const getAssetName = (item) => {
    return item.clientName || item.clientMac || "-";
  };

  const getLocation = (deviceName) => {
    if (!deviceName) return "-";
    return `${deviceName}`;
  };
  console.log(abnormalClientData);
  const unauthorizedMovementsData = useMemo(() => {
    if (!Array.isArray(abnormalClientData)) return [];

    return abnormalClientData
      .filter((item) => item.status === "displaced")
      .map((item) => {
        const rowKey = `unauthorized-${item.clientMac}`;
        const selectedAction = selectedActionMap[rowKey];

        let status = "Displaced";

        if (selectedAction === "allow") {
          status = "Secured";
        }

        if (selectedAction === "clean") {
          status = "Learning";
        }

        return {
          id: item.clientMac,
          status,
          assetName: getAssetName(item),
          oldLocation: getLocation(item.deviceName),
          oldPort: item.devicePort,
          newLocation: getLocation(item.newDeviceName),
          newPort: item.newDevicePort,
        };
      });
  }, [abnormalClientData, selectedActionMap]);

  const securedAssetsData = useMemo(() => {
    if (!Array.isArray(abnormalClientData)) return [];

    return abnormalClientData
      .filter((item) => item.status !== "displaced")
      .map((item) => {
        const rowKey = `secured-${item.clientMac}`;
        const selectedAction = selectedActionMap[rowKey];

        let status = item.status === "secured" ? "Secured" : "Learning";

        if (selectedAction === "clean") {
          status = "Learning";
        }

        return {
          id: item.clientMac,
          port: item.devicePort,
          status,
          assetName: getAssetName(item),
          anchorLocation: getLocation(item.deviceName),
        };
      });
  }, [abnormalClientData, selectedActionMap]);

  const handleActionClick = (rowKey, actionType) => {
    setSelectedActionMap((prev) => ({
      ...prev,
      [rowKey]: prev[rowKey] === actionType ? null : actionType,
    }));

    setShowBottomActionBar(true);
  };

  const handleTrustAll = () => {
    const updatedMap = {};

    unauthorizedMovementsData.forEach((item) => {
      updatedMap[`unauthorized-${item.id}`] = "allow";
    });

    setSelectedActionMap((prev) => ({
      ...prev,
      ...updatedMap,
    }));

    setShowBottomActionBar(true);
  };

  const handleCleanAll = () => {
    const updatedMap = {};

    unauthorizedMovementsData.forEach((item) => {
      updatedMap[`unauthorized-${item.id}`] = "clean";
    });

    setSelectedActionMap((prev) => ({
      ...prev,
      ...updatedMap,
    }));

    setShowBottomActionBar(true);
  };

  const handleCancel = () => {
    setSelectedActionMap({});
    setShowBottomActionBar(false);
  };

  const handleApply = async () => {
    try {
      const trustClientMacs = [];
      const cleanClientMacs = [];

      Object.entries(selectedActionMap).forEach(([rowKey, action]) => {
        if (!action) return;

        const clientMac = rowKey
          .replace("unauthorized-", "")
          .replace("secured-", "");

        if (action === "allow") {
          trustClientMacs.push(clientMac);
        }

        if (action === "clean") {
          cleanClientMacs.push(clientMac);
        }
      });

      const apiPath = auth?.orgs?.id
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/security/abnormalLocation/action.update"
        : "/orgs/:orgId/sites/:siteId/security/abnormalLocation/action.update";

      const restfulParams = auth?.orgs?.id
        ? {
            entId: auth?.realm?.id,
            orgId: auth?.orgs?.id,
            siteId: auth?.sites?.id,
          }
        : {
            orgId: auth?.realm?.id,
            siteId: auth?.sites?.id,
          };

      if (trustClientMacs.length > 0) {
        await sendRequest(apiPath, uuidv4(), restfulParams, {
          data: {
            clientMacs: trustClientMacs,
            action: "trust",
          },
        });
      }

      if (cleanClientMacs.length > 0) {
        await sendRequest(apiPath, uuidv4(), restfulParams, {
          data: {
            clientMacs: cleanClientMacs,
            action: "clean",
          },
        });
      }
      await refreshAbnormalLocationData();
      setSelectedActionMap({});
      setShowBottomActionBar(false);
    } catch (error) {
      console.error("Apply abnormal location action failed:", error);
    }
  };

  const hasSelectedAction = useMemo(() => {
    return Object.values(selectedActionMap).some(Boolean);
  }, [selectedActionMap]);

  const unauthorizedMovementsColumns = useMemo(
    () => [
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("assetName", {
        header: "Asset Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("oldLocation", {
        header: "Old Location",
        cell: (info) => {
          const row = info.row.original;

          return row.oldPort
            ? `${row.oldLocation}/${row.oldPort}`
            : row.oldLocation;
        },
      }),
      columnHelper.accessor("newLocation", {
        header: "New Location",
        cell: (info) => {
          const row = info.row.original;

          return row.newPort
            ? `${row.newLocation}/${row.newPort}`
            : row.newLocation;
        },
      }),
      columnHelper.display({
        id: "action",
        header: "Action",
        cell: (info) => {
          const rowKey = `unauthorized-${info.row.original.id}`;
          const selectedAction = selectedActionMap[rowKey];

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                onClick={() => handleActionClick(rowKey, "allow")}
                sx={{
                  display: "flex",
                  cursor: "pointer",
                }}
              >
                {selectedAction === "allow" ? (
                  <AllowSolidIcon />
                ) : (
                  <AllowIcon />
                )}
              </Box>

              <Box
                sx={{
                  width: "1px",
                  height: 18,
                  bgcolor: "#D5D5D5",
                }}
              />

              <Box
                onClick={() => handleActionClick(rowKey, "clean")}
                sx={{
                  display: "flex",
                  cursor: "pointer",
                }}
              >
                {selectedAction === "clean" ? (
                  <BlockSolidIcon />
                ) : (
                  <BlockIcon />
                )}
              </Box>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, selectedActionMap],
  );

  const securedAssetsColumns = useMemo(
    () => [
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("assetName", {
        header: "Asset Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("anchorLocation", {
        header: "Anchor Location",
        cell: (info) => {
          const row = info.row.original;

          return row.port
            ? `${row.anchorLocation}/${row.port}`
            : row.anchorLocation;
        },
      }),
      /* columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          if (info.row.original.status === "Learning") {
            return null;
          }

          const rowKey = `secured-${info.row.original.id}`;
          const selectedAction = selectedActionMap[rowKey];

          return (
            <Box
              onClick={() => handleActionClick(rowKey, "clean")}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              {selectedAction === "clean" ? <BlockSolidIcon /> : <BlockIcon />}
            </Box>
          );
        },
      }), */
    ],
    [columnHelper, selectedActionMap],
  );

  const unauthorizedMovementsTable = useTableInstance({
    tableName: "unauthorizedMovementsDrawerTable",
    columns: unauthorizedMovementsColumns,
    data: unauthorizedMovementsData,
    enableSorting: false,
    enablePagination: false,
  });

  const securedAssetsTable = useTableInstance({
    tableName: "securedAssetsDrawerTable",
    columns: securedAssetsColumns,
    data: securedAssetsData,
    enableSorting: false,
    enablePagination: false,
  });

  return (
    <Box
      id={containerId}
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: showBottomActionBar ? 12 : 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_pie_chart}
          sx={{ width: 20, height: 20, mr: 1.5 }}
        />

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
          }}
        >
          Abnormal Location & Asset Displacement
        </Typography>
      </Box>

      <CollapseCard title="Assets Status Monitor">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Box sx={{ width: "100%" }}>
            <FirmwareComplianceChart
              data={pieChartData}
              height="260px"
              totalLabel="Total"
              totalValue={totalValue}
              legendFormatter={legendFormatter}
            />
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                pb: 2,
                borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.main,
                }}
              >
                Unauthorized Movements
              </Typography>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <OutlineRectangleButton
                  width="120px"
                  height="32px"
                  startIcon={<AllowIcon />}
                  onClick={handleTrustAll}
                >
                  Trust All
                </OutlineRectangleButton>

                <OutlineWarnRectangleButton
                  width="120px"
                  height="32px"
                  startIcon={<BlockIcon />}
                  sx={{ marginRight: "20px" }}
                  onClick={handleCleanAll}
                >
                  Clean All
                </OutlineWarnRectangleButton>
              </Box>
            </Box>

            <TableField {...unauthorizedMovementsTable} />
          </Box>

          <Box>
            <Box
              sx={{
                pb: 2,
                borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.main,
                }}
              >
                Secured Assets
              </Typography>
            </Box>

            <TableField {...securedAssetsTable} />
          </Box>
        </Box>
      </CollapseCard>

      <BottomActionBar
        parentId={containerId}
        show={showBottomActionBar}
        onCheckFormValid={() => hasSelectedAction}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </Box>
  );
};

export default AbnormalLocationAndAssestDisplacementDrawer;
